import { mediaDesktop } from 'breakpoints';
import theme from 'components/theme';
import Button from 'components/Button';
import Media from 'components/Media';
import styled from 'styled-components/macro';
import type { PromoHeadlineSize } from './types';

interface PromoWrapperProps {
  $hasTimer?: boolean,
  $background?: string,
  $mobile?: string
}

export const PromoWrapper = styled.div<PromoWrapperProps>`
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  position: relative;
  overflow: hidden;

  background: ${theme.colors.black} no-repeat top/contain;

  ${mediaDesktop} {
    height: 600px;
    justify-content: ${({ $hasTimer }) => ($hasTimer ? 'flex-end' : 'center')};
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0), #000000),
      linear-gradient(to bottom, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)),
      url(${({ $background }) => $background});
    background-size: cover;
    background-position: top;
  }
`;

export const PromoCover = styled(Media)<{ $hasTimer?: boolean, gradient?: string }>`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  padding-bottom: ${({ $hasTimer }) => ($hasTimer ? '151px' : '0')};

  ${mediaDesktop} {
    padding-bottom: 0;
  }

  &:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background-image: ${({ gradient }) => gradient || 'linear-gradient(to bottom, rgba(0, 0, 0, 0), #000 93%)'};
    ${mediaDesktop} {
      background-image: ${({ gradient }) => gradient || 'linear-gradient(to bottom, rgba(0, 0, 0, 0) 33%, #000)'};
    }
  }
`;

export const ButtonsWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
  position: relative;
  padding-bottom: 24px;

  ${mediaDesktop} {
    max-width: 1123px;
    margin-top: 32px;
    padding-bottom: 43px;
  }
`;

export const PromoButton = styled(Button) <{ $align: 'left' | 'right' }>`
  position: absolute;
  top: 0;
  left: ${({ $align }) => ($align === 'left' ? '12px' : 'auto')};
  right: ${({ $align }) => ($align === 'right' ? '12px' : 'auto')};
  font-family: ${theme.fonts.primary};
  font-size: 12px;
  text-shadow: 0 0 4px #0009;
  padding: 0 10px;
  min-width: 66px;
  height: 28px;
  border-width: 0;

  ${mediaDesktop} {
    padding: 0px 15px;
    min-width: 92px;
    font-size: 18px;
    line-height: 18px;
    height: 42px;
  }
`;

export const PromoContent = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
`;

export const PromoHeadlineWrapper = styled.div<{ $isDeadline: boolean }>`
  padding: ${({ $isDeadline }) => ($isDeadline ? '37px' : '46px')} 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  margin: auto;
  max-width: 944px;
  flex-grow: 1;

  ${mediaDesktop} {
    min-height: 198px;
    padding: 0 12px;
  }
`;

export const PromoHeadline = styled.h1<{ $size: PromoHeadlineSize }>`
  margin: 8px 20px 0;
  color: ${theme.colors.white};
  font: lighter 42px/42px ${theme.fonts.secondary};
  text-transform: uppercase;
  text-align: center;
  letter-spacing: 0.3px;
  text-shadow: 0 0 4px #0000004d;
  overflow-wrap: anywhere;

  ${mediaDesktop} {
    margin: 0 100px;
    font-size: ${({ $size }) => ($size === 'regular' ? '118px' : '70px')};
    line-height: ${({ $size }) => ($size === 'regular' ? '118px' : '70px')};
    letter-spacing: 1px;
  }
`;

export const PromoSubline = styled.h2`
  color: ${theme.colors.white};
  font: 600 19px ${theme.fonts.primary};
  letter-spacing: 0;
  text-shadow: 0 0 4px #0000004d;
  overflow-wrap: anywhere;
  text-align: center;

  ${mediaDesktop} {
    margin: 9px 0 0;
    font-size: 27px;
    line-height: 27px;
    letter-spacing: 2.4px;
    text-transform: uppercase;
  }
`;

export const PromoCountdownHeadline = styled.h3`
  margin: 20px 0 15px;
  color: ${theme.colors.primary};
  font: lighter 21px ${theme.fonts.secondary};
  letter-spacing: 1px;

  ${mediaDesktop} {
    margin: 3px 0 14px;
    letter-spacing: 1.5px;
    font-size: 30px;
    color: ${theme.colors.white};
  }
`;

export const PromoCountdownNumberRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  column-gap: 30px;

  ${mediaDesktop} {
    column-gap: 28px;
  }
`;

export const PromoCountdownNumberBox = styled.div`
  border: 1px solid ${theme.colors.primary};
  color: ${theme.colors.white};
  width: 48px;
  height: 48px;
  padding-top: 6px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  font: lighter 31px ${theme.fonts.secondary};
  letter-spacing: 1px;
  text-align: center;

  ${mediaDesktop} {
    width: 50px;
    height: 50px;
    border-width: 2px;
    border-color: ${theme.colors.white};
    font-size: 40px;
  }
`;

export const PromoCountdownNumberLabel = styled.h5`
  margin-top: 10px;
  letter-spacing: 1px;
  color: ${theme.colors.white};
  font: lighter 13px ${theme.fonts.secondary};
  text-align: center;

  ${mediaDesktop} {
    margin-top: 16px;
    font-size: 16px;
  }
`;

export const PromoCountdownWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: start;
  flex-direction: column;
  width: 100%;
  height: 151px;
  background: #171717;

  ${mediaDesktop} {
    height: 200px;
    background: transparent;
  }
`;
